import { ScoringResultsComponent } from './components/scoring-results/scoring-results.component';
import { inject, Injectable, NgModule } from '@angular/core';
import { Router, RouterModule, Routes } from '@angular/router';
import { Observable, of } from 'rxjs';
import { ScanSessionReviewComponent } from './components/scan-session-review/scan-session-review.component';
import { MaintenanceComponent, UnauthorizedComponent, UnsupportedBrowserComponent } from '@drc-eca/eca-lrc-common-ui';
import { BiographicsViewComponent } from './components/biographics/view/view.component';
import { BiographicEditComponent } from './components/biographics/edit/edit.component';
import { DiagnosticsComponent } from './components/diagnostics/diagnostics.component';
import { ScanDebugImagesComponent } from './components/diagnostics/scan-debug-images/scan-debug-images.component';
import { ScansTableComponent } from './components/diagnostics/scans-table/scans-table.component';
import { ViewScanImageComponent } from './components/diagnostics/view-scan-image/view-scan-image.component';
import { AppContextService, EcaSecurityService } from '@drc-eca/eca-components-lib';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ScannerComponent } from './components/scanner/scanner.component';
import { AlertsComponent } from './components/alerts/alerts.component';
import { AlertsReviewComponent } from './components/alerts/review/review.component';
import { ReviewEditNavComponent } from './components/shared/review-edit-nav/review-edit-nav.component';
import { PermissionService } from './services/permission.service';


@Injectable()
export class RedirectOnUnauthorized  {
  private router = inject(Router);
  private ecaSecurityService = inject(EcaSecurityService);
  private appContextService = inject(AppContextService);


  canActivate() {
    return this.appContextService.getToken()
      .pipe(
        switchMap(token => this.ecaSecurityService.authenticateToken(token)),
        catchError(() => of(false)),
        map(isLoggedIn => {
          if (!isLoggedIn) {
            this.router.navigate(['unauthorized']);
          }
          return !!isLoggedIn;
        })
      );
  }
}

@Injectable()
export class RedirectOnUnsupportedBrowser  {
  private router = inject(Router);


  canActivate() {
    const ua = window.navigator.userAgent;
    // IE 10 and below
    const msie = ua.indexOf('MSIE ') !== -1;
    // IE 11
    const trident = ua.indexOf('Trident/') !== -1;

    const supported = !msie && !trident;

    if (!supported) {
      this.router.navigate(['/unsupported-browser']);
    }
    return supported;
  }
}

@Injectable()
export class DiagnosticsGuard  {
  private router = inject(Router);
  private permissionService = inject(PermissionService);


  canActivate(): Observable<boolean> {
    return this.permissionService.hasAnyPermission(PermissionService.ACE_TABE_VIEW_DIAGNOSTICS)
      .pipe(
        tap(authorized => {
          if (!authorized) {
            this.router.navigate(['/scan']);
          }
        })
      );
  }
}

const routes: Routes = [
  {
    path: 'scan',
    component: ScannerComponent,
    canActivate: [RedirectOnUnauthorized, RedirectOnUnsupportedBrowser],
  },
  /* TODO is this route ever even called?
  {
    path: 'review',
    component: ScanSessionReviewComponent,
    canActivate: [RedirectOnUnauthorized, RedirectOnUnsupportedBrowser],
  },*/
  {
    path: 'review',
    component: ScanSessionReviewComponent,
    canActivate: [RedirectOnUnauthorized, RedirectOnUnsupportedBrowser],
  },
  {
    path: 'alerts',
    component: AlertsComponent,
    canActivate: [RedirectOnUnauthorized, RedirectOnUnsupportedBrowser],
  },
  {
    path: 'biographics',
    component: BiographicsViewComponent,
    canActivate: [RedirectOnUnauthorized, RedirectOnUnsupportedBrowser],
  },
  {
    path: 'biographic/:pendingId',
    component: ReviewEditNavComponent,
    canActivate: [RedirectOnUnauthorized, RedirectOnUnsupportedBrowser],
    children: [
      {
        path: 'edit',
        component: BiographicEditComponent,
      },
      {
        path: 'review',
        component: AlertsReviewComponent,
      }
    ]
  },
  {
    path: 'scoring-results',
    component: ScoringResultsComponent,
    canActivate: [RedirectOnUnauthorized, RedirectOnUnsupportedBrowser],
  },
  {
    path: 'diagnostics/:sessionId',
    component: DiagnosticsComponent,
    canActivate: [RedirectOnUnauthorized, DiagnosticsGuard, RedirectOnUnsupportedBrowser],
  },
  {
    path: 'diagnostics/:sessionId/scans',
    component: ScansTableComponent,
    canActivate: [DiagnosticsGuard, RedirectOnUnsupportedBrowser],
  },
  {
    path: 'diagnostics/viewScanImage/:scanId',
    component: ViewScanImageComponent,
  },
  {
    path: 'diagnostics/:sessionId/scans/:imageId',
    component: ScanDebugImagesComponent,
    canActivate: [DiagnosticsGuard, RedirectOnUnsupportedBrowser],
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
  },
  {
    path: 'maintenance',
    component: MaintenanceComponent,
  },
  {
    path: 'unsupported-browser',
    component: UnsupportedBrowserComponent,
  },
  {
    path: '**',
    redirectTo: 'scan',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: true })],
  providers: [RedirectOnUnauthorized, RedirectOnUnsupportedBrowser, DiagnosticsGuard],
  exports: [RouterModule],
})
export class AppRoutingModule {
  private router = inject(Router);

  constructor() {
    this.router.initialNavigation();
  }
}
